import { useState, useEffect, useCallback, useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Database, Upload, Loader2, ArrowLeft, RefreshCw, AlertTriangle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { GlassCard } from '@/components/custom/GlassCard';
import { DmpConsolePanel, addressLine } from '@/components/dmp/DmpConsolePanel';
import type { DmpConsoleRequest } from '@/components/dmp/DmpConsolePanel';
import { EditAddressForm } from '@/components/dmp/EditAddressForm';
import { SwapBusNumberModal } from '@/components/dmp/SwapBusNumberModal';
import { FakeOdfModal } from '@/components/dmp/FakeOdfModal';
import { ForceBlacklistFeedbackModal } from '@/components/dmp/ForceBlacklistFeedbackModal';
import { EditAddressColumnModal } from '@/components/dmp/EditAddressColumnModal';
import { ConsoleMappingForm } from '@/components/dmp/ConsoleMappingForm';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { CreateAddressForm } from '@/components/tickets/CreateAddressForm';
import { AddBoxForm, type AddressMgmtContext } from '@/components/tickets/AddressManagementMenu';
import { useAuth } from '@/hooks/useAuth';
import { fetchBuildFiles, startBuildFilesSync, uploadBuildFile } from '@/hooks/useBuildFiles';
import { ApiError } from '@/lib/api-client';
import type {
  DmpLookupResponse, BuildFileKind, BuildFileStatus, BuildFilesSyncStatus,
  DmpConsoleMappingResponse, DmpConsoleMappingRow,
} from '@uniticket/shared';

const BUILD_FILE_KINDS: BuildFileKind[] = ['SERVICE', 'DEPLOYMENT', 'BLACKLIST'];

// The two exports are regenerated every evening: one older than two days means the chain broke somewhere
// (no export, SharePoint unreachable, the sync switched off) — said in amber (user decision 2026-09-25).
// The blacklist changes when Proximus sends a new one, so its age says nothing.
const STALE_AFTER_MS = 2 * 24 * 3600 * 1000;

// Same format as the other admin screens (JVID registry, blacklist feedback): Belgian, whatever the browser.
const fmt = (iso: string) => new Date(iso).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' });

const pill = (disabled: boolean) => cn(
  'inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors',
  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-brand-rose/40',
);

/** Admin-only: the Build reference files the console searches against. Since R1 (2026-09-25) the server
 *  fetches them from SharePoint every morning at 6 — the card says which file is loaded, how old it is,
 *  where it came from, and how the last check went; « Actualiser » checks now. The manual upload stays
 *  as the fallback: gzipped client-side (~47 MB → ~5 MB), ~200k rows parsed server-side, and the previous
 *  file keeps answering lookups until the new one has fully landed. */
function BuildFilesCard() {
  const { t } = useTranslation(['tickets']);
  const [statuses, setStatuses] = useState<BuildFileStatus[]>([]);
  const [sync, setSync] = useState<BuildFilesSyncStatus | null>(null);
  const [busy, setBusy] = useState<BuildFileKind | null>(null);
  const [starting, setStarting] = useState(false);
  // Was a sync running at the previous read? Its end is announced once, whoever started it.
  const running = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchBuildFiles();
      setStatuses(res.data);
      setSync(res.sync);
      if (running.current && !res.sync.running) {
        const failed = res.sync.results.some((r) => r.outcome === 'error');
        if (failed) toast.error(t('tickets:dmpLookup.buildFile.sync.doneWithErrors'));
        else toast.success(t('tickets:dmpLookup.buildFile.sync.done'));
      }
      running.current = res.sync.running;
    } catch {
      // A status that cannot be read must not break the console — the search itself still works.
    }
  }, [t]);
  useEffect(() => { void load(); }, [load]);

  // A load takes a minute or two: follow it while it runs.
  useEffect(() => {
    if (!sync?.running) return;
    const id = window.setInterval(() => { void load(); }, 3000);
    return () => window.clearInterval(id);
  }, [sync?.running, load]);

  const refresh = async () => {
    setStarting(true);
    try {
      await startBuildFilesSync();
      running.current = true;
      toast.success(t('tickets:dmpLookup.buildFile.sync.started'));
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        running.current = true;
        toast.info(t('tickets:dmpLookup.buildFile.sync.alreadyRunning'));
      } else {
        toast.error((err as Error)?.message || t('tickets:dmpLookup.buildFile.sync.failed'));
      }
    } finally {
      setStarting(false);
      await load();
    }
  };

  const pick = (kind: BuildFileKind) => async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so re-picking the SAME file still fires a change
    if (!file) return;
    setBusy(kind);
    try {
      const res = await uploadBuildFile(kind, file);
      toast.success(t('tickets:dmpLookup.buildFile.uploaded', { rows: res.data.rowCount }));
      await load();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) toast.error(t('tickets:dmpLookup.buildFile.busy'));
      else toast.error((err as Error)?.message || t('tickets:dmpLookup.buildFile.uploadError'));
    } finally {
      setBusy(null);
    }
  };

  const syncRunning = !!sync?.running;
  const lastHow = sync?.lastTrigger === 'manual'
    ? t('tickets:dmpLookup.buildFile.sync.how.manual', { who: sync.lastActor ?? '?' })
    : t(`tickets:dmpLookup.buildFile.sync.how.${sync?.lastTrigger ?? 'schedule'}`);

  return (
    <GlassCard className="p-5">
      <p className="text-sm font-bold text-slate-800">{t('tickets:dmpLookup.buildFile.uploadTitle')}</p>
      <p className="mt-0.5 text-xs text-slate-500">{t('tickets:dmpLookup.buildFile.uploadHint')}</p>

      {/* The sync: when it last looked, how, and « Actualiser ». */}
      <div className="mt-3 flex flex-wrap items-start justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2">
        <div className="min-w-0 space-y-0.5 text-[11px] text-slate-600">
          {syncRunning ? (
            <p className="inline-flex items-center gap-1.5 font-medium">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('tickets:dmpLookup.buildFile.sync.running')}
            </p>
          ) : sync?.lastRunAt ? (
            <p>{t('tickets:dmpLookup.buildFile.sync.last', { when: fmt(sync.lastRunAt), how: lastHow })}</p>
          ) : (
            <p>{t('tickets:dmpLookup.buildFile.sync.never')}</p>
          )}
          {sync && !sync.configured && (
            <p className="text-red-700">{t('tickets:dmpLookup.buildFile.sync.unconfigured')}</p>
          )}
          {sync?.configured && !sync.enabled && (
            <p className="text-slate-500">{t('tickets:dmpLookup.buildFile.sync.off', { hour: sync.hour })}</p>
          )}
          {sync?.nextRunAt && (
            <p className="text-slate-500">{t('tickets:dmpLookup.buildFile.sync.next', { when: fmt(sync.nextRunAt) })}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={!sync?.configured || syncRunning || starting || busy !== null}
          className={pill(!sync?.configured || syncRunning || starting || busy !== null)}
        >
          {starting || syncRunning
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" />}
          {t('tickets:dmpLookup.buildFile.sync.refresh')}
        </button>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {BUILD_FILE_KINDS.map((kind) => {
          const st = statuses.find((s) => s.kind === kind);
          const result = sync?.results.find((r) => r.kind === kind);
          const stale = !!st && kind !== 'BLACKLIST'
            && Date.now() - new Date(st.fileDate ?? st.uploadedAt).getTime() > STALE_AFTER_MS;
          return (
            <div key={kind} className="rounded-xl border border-slate-200 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                {t(`tickets:dmpLookup.buildFile.${kind}`)}
              </p>
              {st ? (
                <>
                  <p className="mt-0.5 break-all text-[11px] font-medium text-slate-700">{st.filename}</p>
                  <p className={cn(
                    'mt-0.5 flex flex-wrap items-center gap-1 text-[11px]',
                    stale ? 'font-semibold text-amber-700' : 'text-slate-600',
                  )}>
                    {stale && <AlertTriangle className="h-3 w-3 shrink-0" />}
                    {st.fileDate
                      ? t('tickets:dmpLookup.buildFile.fileDate', { date: fmt(st.fileDate) })
                      : t('tickets:dmpLookup.buildFile.fileDateUnknown')}
                    {stale && <span>— {t('tickets:dmpLookup.buildFile.stale')}</span>}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    {t('tickets:dmpLookup.buildFile.loadedLine', { rows: st.rowCount.toLocaleString('fr-BE'), when: fmt(st.uploadedAt) })}
                    {' · '}
                    {st.source === 'SHAREPOINT'
                      ? t('tickets:dmpLookup.buildFile.sourceSharepoint')
                      : t('tickets:dmpLookup.buildFile.sourceUpload', { who: st.uploadedBy })}
                  </p>
                </>
              ) : (
                <p className="mt-0.5 text-[11px] text-slate-500">{t('tickets:dmpLookup.buildFile.none')}</p>
              )}
              {/* What the last check could not do for this file — a success needs no words, the lines above say it. */}
              {result && (result.outcome === 'error' || result.outcome === 'notFound' || result.outcome === 'busy') && (
                <p className={cn('mt-1 text-[11px]', result.outcome === 'error' ? 'text-red-700' : 'text-amber-700')}>
                  {t(`tickets:dmpLookup.buildFile.sync.outcome.${result.outcome}`, { error: result.error ?? '' })}
                </p>
              )}
              <label className={cn('mt-2', pill(busy !== null || syncRunning))}>
                {busy === kind
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Upload className="h-3.5 w-3.5" />}
                {busy === kind ? t('tickets:dmpLookup.buildFile.uploading') : t('tickets:dmpLookup.buildFile.replace')}
                <input
                  type="file"
                  accept=".csv,.gz,text/csv"
                  className="hidden"
                  disabled={busy !== null || syncRunning}
                  onChange={pick(kind)}
                />
              </label>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}

// The DMP console as a PAGE (user ask 2026-08-07). Same tool as the drawer every internal user opens
// from a ticket — same component, so the two can't drift — but reachable on its own URL, without a
// ticket. This is the only screen a BUILD account has.
//
// Since 2026-08-11 the page is no longer read-only, but for ADMINS ONLY: a SYSTEM_ADMIN can create an
// address, or add a box, straight from a looked-up JVID with no ticket behind the request. The panel
// itself stays read-only — the page hands it the two callbacks (and only when isAdmin), and owns the
// forms and the POST, exactly as the drawer does for « lier ce jvid au ticket ». A validateur or a BUILD
// account passes nothing and gets precisely the console of before. Hiding the buttons is comfort, not
// the guarantee: the server gates /dmp/console/* on SYSTEM_ADMIN on its own.

export function DmpConsolePage() {
  const { t } = useTranslation(['tickets']);
  const { isAdmin } = useAuth();
  const [action, setAction] = useState<'create' | 'addBox' | 'edit' | 'swap' | 'fakeOdf' | 'forceBlacklist' | 'editColumn' | 'mapping' | null>(null);
  // La colonne ouverte dans l'éditeur brut (17-09).
  const [columnToEdit, setColumnToEdit] = useState<string | null>(null);
  // N39 — la déclaration ouverte dans le formulaire de mapping : le bloc tel qu'il a été lu, et la ligne à modifier
  // (`row` = `null` → créer). Le bloc est passé tel quel : c'est LUI qui porte le jeton de la ligne et les logements
  // voisins du contrôle de doublon — le relire ici ouvrirait le formulaire sur un autre état que celui qu'on a vu.
  const [mappingEdit, setMappingEdit] = useState<{ mapping: DmpConsoleMappingResponse; row: DmpConsoleMappingRow | null } | null>(null);
  // Arrivée depuis un dossier (bouton « Console DMP » de la fenêtre de validation, admins seuls) :
  // le jvid est cherché tout seul, et le chemin du retour est porté par l'URL — donc il survit à un
  // rechargement et à un lien recopié à quelqu'un d'autre.
  const [searchParams] = useSearchParams();
  const fromTicketId = searchParams.get('from');
  const fromTicketNo = searchParams.get('fromNo');
  // Re-request of the SAME jvid after an edit: the panel owns its state, and only a fresh nonce makes it
  // reload. Without this the console would keep showing the values the admin has just corrected.
  const [request, setRequest] = useState<DmpConsoleRequest | null>(() => {
    const j = Number(searchParams.get('jvid'));
    return Number.isInteger(j) && j > 0 ? { jvid: j, nonce: Date.now() } : null;
  });
  const [anchor, setAnchor] = useState<DmpLookupResponse | null>(null);
  const ref = anchor?.reference;

  // The looked-up address is a TEMPLATE (user decision 2026-08-11): street, postal code, city and
  // municipality carry over, the house number is deliberately left empty — the admin is creating the
  // address next door, not a copy of this one. Passing all four levels at once is safe: the Create
  // form's strict cascade only resets a level when the admin CHANGES a parent, never on mount.
  const createContext: AddressMgmtContext = {
    streetName: ref?.streetName ?? null,
    postalCode: ref?.postalCode ?? null,
    municipality: ref?.municipality ?? null,
    city: ref?.city ?? null,
    viaConsole: true,
  };
  // A box, on the other hand, goes on the looked-up address's OWN building.
  const addBoxContext: AddressMgmtContext = {
    streetName: ref?.streetName ?? null,
    municipality: ref?.municipality ?? null,
    city: ref?.city ?? null,
    belmapBuildingId: ref?.belmapBuildingId ?? null,
    jvid: ref?.jvid ?? null,
    viaConsole: true,
  };

  const start = (mode: 'create' | 'addBox' | 'edit' | 'swap' | 'fakeOdf' | 'forceBlacklist' | 'editColumn') => (data: DmpLookupResponse) => {
    setAnchor(data);
    setAction(mode);
  };

  return (
    <div className="space-y-6">
      {/* Le chemin du retour, en tête et non enfoui dans la page : on est venu d'un dossier pour
          regarder une chose précise, et on y retourne. Rendu seulement quand l'URL dit d'où l'on vient. */}
      {fromTicketId && (
        // Collé en haut de la fenêtre pendant le défilement (demande user 2026-09-02) : la fiche est
        // longue, et le chemin du retour ne doit pas se perdre en haut de page. Les marges négatives
        // annulent celles de <main> pour que la bande couvre toute la largeur — sinon le contenu qui
        // défile apparaîtrait sur les côtés du bouton. z-30 reste SOUS les modales (z-50).
        <div className="sticky top-0 z-30 -mx-4 -mt-6 bg-slate-50/95 px-4 py-3 backdrop-blur-sm md:-mx-8 md:px-8">
          <Button asChild variant="outline" size="sm" className="w-fit shadow-sm">
            <Link to={`/tickets/${fromTicketId}/address-validation`}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {fromTicketNo
                ? t('tickets:dmpLookup.backToTicketNo', { ticket: fromTicketNo })
                : t('tickets:dmpLookup.backToTicket')}
            </Link>
          </Button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <Database className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('tickets:dmpLookup.title')}</h2>
          <p className="text-sm text-slate-500">{t('tickets:dmpLookup.subtitle')}</p>
          {/* Say out loud that a creation started here answers to no ticket — it is the one thing that
              makes this screen different from the same console opened from a dossier. */}
          {isAdmin && (
            <p className="mt-0.5 text-xs text-slate-400">{t('tickets:dmpLookup.adminCreateHint')}</p>
          )}
        </div>
      </div>

      <GlassCard className="p-5">
        <DmpConsolePanel
          request={request}
          onCreateAddress={isAdmin ? start('create') : undefined}
          onAddBox={isAdmin ? start('addBox') : undefined}
          onEditAddress={isAdmin ? start('edit') : undefined}
          onSwapBusNumber={isAdmin ? start('swap') : undefined}
          onForceBlacklist={isAdmin ? start('forceBlacklist') : undefined}
          onEditColumn={isAdmin ? (data, column) => { setColumnToEdit(column); start('editColumn')(data); } : undefined}
          // N39 — le bloc « Table de mapping » se lit par tous ; ses boutons, pour les admins seulement.
          onEditMapping={isAdmin ? (data, mapping, row) => { setMappingEdit({ mapping, row }); setAnchor(data); setAction('mapping'); } : undefined}
          // « Voisin » (25-09) : lecture seule, donc offert à tous ceux qui ont la console — ici seulement.
          showNeighbours
        />
      </GlassCard>

      {isAdmin && <BuildFilesCard />}

      {/* Same sheet + same two forms as the MDU queue uses for its cases 1 and 3 — one implementation of
          "create an address / add a box without a ticket", reused rather than re-written. */}
      <Sheet
        open={action === 'create' || action === 'addBox' || action === 'edit' || action === 'mapping'}
        onOpenChange={(o) => { if (!o) setAction(null); }}
      >
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-6" onOpenAutoFocus={(e) => e.preventDefault()}>
          {action === 'create' && <CreateAddressForm context={createContext} onCreated={() => setAction(null)} />}
          {action === 'addBox' && <AddBoxForm context={addBoxContext} onCreated={() => setAction(null)} />}
          {action === 'edit' && anchor && (
            <EditAddressForm
              address={anchor}
              onSaved={(jvid) => { setAction(null); setRequest({ jvid, nonce: Date.now() }); }}
              // L'aperçu du fake ODF est rendu par la PAGE, hors du Sheet : une modale portalisée
              // par-dessus un Sheet Radix tomberait dans son piège à focus, et le premier clic à
              // l'intérieur serait lu comme un clic dehors — donc fermeture du Sheet sous les doigts.
              onAttachFakeOdf={() => setAction('fakeOdf')}
            />
          )}
          {/* N39 — créer ou modifier la déclaration d'un opérateur. La clé repart d'un formulaire neuf à chaque
              ouverture : sans elle, passer de « Modifier Proximus » à « Créer » garderait la saisie précédente. */}
          {action === 'mapping' && anchor && mappingEdit && (
            <ConsoleMappingForm
              key={`${mappingEdit.mapping.jvid}:${mappingEdit.row?.operatorId ?? 'new'}:${mappingEdit.row?.updatedAtToken ?? ''}`}
              address={anchor}
              mapping={mappingEdit.mapping}
              row={mappingEdit.row}
              onSaved={(jvid) => { setAction(null); setRequest({ jvid, nonce: Date.now() }); }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* A modal rather than the sheet (user ask): the swap is a two-sided comparison, and the side-by-
          side table needs the width. Rendered HERE, outside the GlassCard, because the card's
          backdrop-filter would otherwise become the containing block of its fixed overlay. */}
      {/* Poser un fake ODF sur l'adresse chargée. Ouvert depuis la ligne « Fibre rattachée » du verdict
          (dans le panneau) OU depuis le panneau « Modifier l'adresse » — deux portes, un seul écran et
          un seul chemin d'écriture. Rendu ici pour la même raison que le swap. */}
      {action === 'fakeOdf' && anchor && (
        <FakeOdfModal
          jvid={anchor.reference.jvid}
          addressLabel={addressLine(anchor.reference.streetName, anchor.reference.houseNumber, anchor.reference.houseNumberExtension)}
          onClose={() => setAction(null)}
          onAttached={(jvid) => { setAction(null); setRequest({ jvid, nonce: Date.now() }); }}
        />
      )}

      {/* « Forcer » (17-09) : la ligne sans modification pour le fichier de retour blacklist, ouverte depuis la
          fiche de la pastille rouge. Rendue ici, hors de la GlassCard, pour la même raison que le swap. */}
      {action === 'forceBlacklist' && anchor && (
        <ForceBlacklistFeedbackModal
          jvid={anchor.reference.jvid}
          addressLabel={addressLine(anchor.reference.streetName, anchor.reference.houseNumber, anchor.reference.houseNumberExtension)}
          onClose={() => setAction(null)}
          onForced={() => setAction(null)}
        />
      )}

      {/* L'éditeur brut d'une colonne (17-09). Une colonne d'identité renvoie vers « Modifier l'adresse » : même
          adresse chargée, autre formulaire — celui qui porte le contrôle de doublon et la règle blacklist. */}
      {action === 'editColumn' && anchor && columnToEdit && (
        <EditAddressColumnModal
          jvid={anchor.reference.jvid}
          column={columnToEdit}
          addressLabel={addressLine(anchor.reference.streetName, anchor.reference.houseNumber, anchor.reference.houseNumberExtension)}
          onClose={() => setAction(null)}
          onSaved={(jvid) => { setAction(null); setRequest({ jvid, nonce: Date.now() }); }}
          onOpenIdentity={() => setAction('edit')}
        />
      )}

      {action === 'swap' && anchor && (
        <SwapBusNumberModal
          address={anchor}
          onClose={() => setAction(null)}
          onSwapped={(jvid) => { setAction(null); setRequest({ jvid, nonce: Date.now() }); }}
        />
      )}
    </div>
  );
}
